// Phase E.1 — All times are SITE-local. Browser timezone never enters booking
// math. These helpers use the Intl API only (no new dependency).
//
// Conventions:
//   • Stored:    ISO 8601 UTC string (Supabase TIMESTAMPTZ)
//   • Picked:    {year, month, day, hour, minute} in site.timezone
//   • Displayed: formatted in site.timezone with a visible disclaimer

/** Resolve site timezone with a safe default. */
export function siteTz(site) {
  return site?.timezone || "Australia/Sydney";
}

/** Format a UTC ISO string in the given site timezone. */
export function formatInSiteTz(iso, tz, opts = {}) {
  if (!iso) return "";
  const d = typeof iso === "string" ? new Date(iso) : iso;
  if (isNaN(d.getTime())) return "";
  const fmt = new Intl.DateTimeFormat("en-AU", {
    timeZone: tz || "Australia/Sydney",
    year:   "numeric",
    month:  "short",
    day:    "2-digit",
    hour:   "2-digit",
    minute: "2-digit",
    hour12: false,
    ...opts,
  });
  return fmt.format(d);
}

/** Short variant — "08 Jun · 14:30" */
export function formatShortInSiteTz(iso, tz) {
  return formatInSiteTz(iso, tz, { year: undefined });
}

/** Time-only variant — "14:30" */
export function formatTimeInSiteTz(iso, tz) {
  return formatInSiteTz(iso, tz, { year: undefined, month: undefined, day: undefined });
}

/** Human-friendly timezone label — "Sydney" from "Australia/Sydney". */
export function tzLabel(tz) {
  if (!tz) return "Sydney";
  const parts = tz.split("/");
  return (parts[parts.length - 1] || tz).replace(/_/g, " ");
}

/** "Sea Lake VIC time · AEDT" — site name (+ state) and the zone abbreviation at `iso` (DST-aware). */
export function placeTimeLabel(site, iso) {
  const tz = siteTz(site);
  let place = (site?.name || tzLabel(tz)).trim();
  const state = (site?.state || "").trim();
  if (state && !place.toUpperCase().endsWith(state.toUpperCase())) place = `${place} ${state}`;
  const zone = new Intl.DateTimeFormat("en-AU", { timeZone: tz, timeZoneName: "short" })
    .formatToParts(iso ? new Date(iso) : new Date()).find((p) => p.type === "timeZoneName")?.value || "";
  return `${place} time${zone ? ` · ${zone}` : ""}`;
}

/** "@ site time (Sydney)" — consistent inline disclaimer. */
export function siteTimeBadge(tz) {
  return `@ site time (${tzLabel(tz)})`;
}

/**
 * Convert a site-local wall clock to a UTC ISO string for storage.
 *
 *   composeInSiteTz({year:2026, month:6, day:8, hour:6, minute:0}, "Australia/Sydney")
 *     → "2026-06-07T20:00:00.000Z"   (06:00 Sydney = 20:00 UTC prev day in winter)
 *
 * Implementation: build a "tagged" UTC date, format it in target tz, then
 * compute the offset and re-anchor. Works for all DST cases the Intl API
 * supports — no library needed.
 */
export function composeInSiteTz({ year, month, day, hour = 0, minute = 0 }, tz) {
  // 1. Pretend the wall clock is already UTC
  const guessUtc = new Date(Date.UTC(year, month - 1, day, hour, minute, 0));

  // 2. Format that instant *as if it were* in the target timezone
  const partsAsTz = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    year:   "numeric", month: "2-digit", day:    "2-digit",
    hour:   "2-digit", minute: "2-digit", second: "2-digit",
    hour12: false,
  }).formatToParts(guessUtc).reduce((m, p) => (m[p.type] = p.value, m), {});

  // Note: at midnight in some zones, 'hour' may come back as "24" — normalise
  let h = parseInt(partsAsTz.hour, 10);
  if (h === 24) h = 0;

  // 3. Compute the offset (in minutes) between our guess and the projected zone
  const projected = Date.UTC(
    parseInt(partsAsTz.year,   10),
    parseInt(partsAsTz.month,  10) - 1,
    parseInt(partsAsTz.day,    10),
    h,
    parseInt(partsAsTz.minute, 10),
    parseInt(partsAsTz.second, 10)
  );
  const offsetMin = (projected - guessUtc.getTime()) / 60000;

  // 4. Subtract the offset to land at the right instant
  return new Date(guessUtc.getTime() - offsetMin * 60000).toISOString();
}

/** Same parts back from a UTC ISO, interpreted in a site tz. */
export function decomposeInSiteTz(iso, tz) {
  const d = new Date(iso);
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    year:   "numeric", month: "2-digit", day:    "2-digit",
    hour:   "2-digit", minute: "2-digit",
    hour12: false,
  }).formatToParts(d).reduce((m, p) => (m[p.type] = p.value, m), {});
  let h = parseInt(parts.hour, 10);
  if (h === 24) h = 0;
  return {
    year:   parseInt(parts.year,   10),
    month:  parseInt(parts.month,  10),
    day:    parseInt(parts.day,    10),
    hour:   h,
    minute: parseInt(parts.minute, 10),
    // convenience strings for <input type=date|time>
    dateStr: `${parts.year}-${parts.month}-${parts.day}`,
    timeStr: `${String(h).padStart(2, "0")}:${parts.minute}`,
  };
}

/** Today's date ("YYYY-MM-DD") on the wall clock of `tz`. */
export function todayInTz(tz) {
  return decomposeInSiteTz(new Date().toISOString(), tz || "Australia/Sydney").dateStr;
}

/** Minutes the zone is ahead of UTC at instant `at` (ms). */
export function tzOffsetMin(tz, at = Date.now()) {
  const p = Object.fromEntries(new Intl.DateTimeFormat("en-US", {
    timeZone: tz, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
  }).formatToParts(at).map((x) => [x.type, x.value]));
  return Math.round((Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute) - Math.floor(at / 60000) * 60000) / 60000);
}

/** Short zone name, e.g. "AEDT". */
export function tzZoneName(tz, at = Date.now()) {
  return new Intl.DateTimeFormat("en-AU", { timeZone: tz, timeZoneName: "short" })
    .formatToParts(at).find((p) => p.type === "timeZoneName")?.value || "";
}

/** "0.5 h" / "2 h" */
export const fmtHours = (m) => { const h = Math.abs(m) / 60; return `${Number.isInteger(h) ? h : h.toFixed(1)} h`; };
